import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  TextField,
  makeStyles
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SaveIcon from '@material-ui/icons/Save';
import { unwrapResult } from '@reduxjs/toolkit';

import { Page } from 'src/components';
import { CustomKeyboardEvent } from 'src/types';
import { slices, useAppDispatch } from 'src/redux';
import { useSnackBar } from 'src/hooks';
import {
  SupplierCompanyDetailRequest
} from 'src/types/supplier';

const { actions: supplierActions } = slices.supplier;

const useStyles = makeStyles(() => ({
  root: {}
}));

const AddSupplierView = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const [supplierName, setSupplierName] = useState('');
  const [detailName, setDetailName] = useState('');
  const [address, setAddress] = useState('');
  const [terms, setTerms] = useState('');
  const [tin, setTin] = useState('');
  const [zipCode, setZipCode] = useState('');

  const onClearNameField = () => {
    setSupplierName('');
    setDetailName('');
    setAddress('');
    setTerms('');
    setTin('');
    setZipCode('');
  };

  const onCreateSupplier = async () => {
    if (
      supplierName &&
      detailName &&
      address &&
      terms &&
      tin &&
      zipCode
    ) {
      const companyDetails: SupplierCompanyDetailRequest[] = [
        {
          name: detailName.trim(),
          address: address.trim(),
          terms: terms.trim(),
          tin: tin.trim(),
          zip_code: zipCode.trim()
        }
      ];
      const response = unwrapResult(
        await dispatch(
          supplierActions.createSupplierThunk({
            company_name: supplierName.trim(),
            company_details: companyDetails
          })
        )
      );
      if (response.success) {
        snackBar.show({ severity: 'success', message: response.message });
        onClearNameField();
      } else {
        snackBar.show({ severity: 'error', message: response.message });
      }
    } else {
      snackBar.show({
        severity: 'error',
        message: 'Please fill up all required information.'
      });
    }
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onCreateSupplier();
    }
  };

  return (
    <Page className={classes.root} title="New supplier">
      <Container maxWidth={false}>
        <Box mt={2}>
          <Card>
            <CardHeader
              title="New Supplier"
              subheader="The information of new supplier."
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={12} xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company_name"
                    value={supplierName}
                    required
                    onKeyPress={(e) => onKeyPress(e)}
                    onChange={(e) => setSupplierName(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <TextField
                    fullWidth
                    label="Detail Name"
                    value={detailName}
                    required
                    onChange={(e) => setDetailName(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={address}
                    required
                    multiline
                    onChange={(e) => setAddress(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    label="Terms"
                    value={terms}
                    required
                    onChange={(e) => setTerms(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    label="TIN"
                    value={tin}
                    required
                    onChange={(e) => setTin(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={zipCode}
                    required
                    inputProps={{ maxLength: 4 }}
                    onChange={(e) => setZipCode(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <Box display="flex" justifyContent="space-between" p={2}>
              <Button
                startIcon={<ClearIcon />}
                onClick={onClearNameField}
                color="secondary"
                variant="contained"
              >
                Clear
              </Button>
              <Button
                startIcon={<SaveIcon />}
                style={{ marginLeft: 18 }}
                onClick={onCreateSupplier}
                color="primary"
                variant="contained"
              >
                Add new supplier
              </Button>
            </Box>
          </Card>
        </Box>
      </Container>
    </Page>
  );
};

export default AddSupplierView;
